<script lang="ts" setup>
import type { IntegrationLoginProvider, SocialLoginProvider, UnifiedAuthStep } from '~kui/types/auth';

import { AuthFlowLayout, PasswordForgotAction } from '~kui/types/auth';
import { isIntegrationLoginProvider } from '~kui/utils/auth';

import type { SignInContinuation } from '~/types/login';

import { useInitiateSocialLogin } from '~/composables/Auth/useInitiateSocialLogin';
import { useIntegrationLogin } from '~/composables/Auth/useIntegrationLogin';
import { useLoginDoorConfig } from '~/composables/Auth/useLoginDoorConfig';
import { useStudentRoleInvite } from '~/composables/Auth/useStudentRoleInvite';
import { useUnifiedAuthFlow } from '~/composables/Auth/useUnifiedAuthFlow';
import { useUnifiedPublicNavHidden, useUnifiedPublicNavLogoOnly } from '~/composables/useUnifiedPublicNav';
import { LoginFlowType, SiteRoleType } from '~/graphql/generated/graphql';
import { useConfigStore } from '~/stores/config';
import { parseEntryTrackingFromQuery } from '~/utils/unified/entryTracking';

/**
 * `roleType` is the door this login is for: students sign up here, the staff doors only let
 * existing roles in.
 */
interface Props {
  roleType?: SiteRoleType;
}

const props = withDefaults(defineProps<Props>(), {
  roleType: SiteRoleType.Student,
});

const { t } = useI18n();

const {
  currentStep,
  authState,
  loading,
  direction,
  finalStepComplete,
  globalError,
  prefilledOtpCode,
  socialAuthPending,
  roleContextReady,
  signedUp,
  goBack,
  reset,
  submitEmail,
  resendLoginCode,
  submitOtp,
  submitName,
  submitSchool,
  submitDetails,
  submitConsent,
  finishWelcome,
  chooseOtpMethod,
  choosePasswordMethod,
  submitPassword,
  forgotPassword,
  continueAfterAccountChoice,
  restartSignIn,
  selectedEntity,
  submitPasswordSetup,
  skipPasswordSetup,
  mfaEnrolment,
  mfaRecoveryCodes,
  mfaLoadFailed,
  mfaExpired,
  mfaSignedIn,
  beginMfaEnrolment,
  mfaEnrolSubmitCode,
  mfaRecoveryCodesSaved,
  mfaSubmitCode,
  mfaSubmitRecoveryCode,
  mfaSignOut,
} = useUnifiedAuthFlow(props.roleType);

const door = useLoginDoorConfig(props.roleType);
const disclaimer = computed(() => door.disclaimerFor(currentStep.value));
// Only the public layout sits under the public nav; the embedded doors render in their own page shell.
const isPublicLayout = door.layout === AuthFlowLayout.Public;

const isRedirecting = computed(() => finalStepComplete.value || socialAuthPending.value);

// The public nav is meaningless mid-login and flashes while the post-login redirect runs.
const navHidden = useUnifiedPublicNavHidden();
watch(isRedirecting, (value) => {
  if (isPublicLayout) {
    navHidden.value = value;
  }
}, { immediate: true });

// Signing up creates the account and signs it in, so from the steps after it the visitor is a
// logged-in student on a page whose nav still offers "Log in". The bar stays, logo only, so the
// card does not jump the way it would if the bar went. These steps are also reachable without a
// signup — a returning student is parked at `school` to finish joining — and there the nav is the
// only way off the login route, so it stays whole.
const POST_SIGNUP_STEPS: readonly UnifiedAuthStep[] = ['password-setup', 'school', 'welcome'];
const navLogoOnly = useUnifiedPublicNavLogoOnly();
watch([currentStep, signedUp], ([step, hasSignedUp]) => {
  if (isPublicLayout) {
    navLogoOnly.value = hasSignedUp && POST_SIGNUP_STEPS.includes(step);
  }
}, { immediate: true });

onUnmounted(() => {
  if (isPublicLayout) {
    navHidden.value = false;
    navLogoOnly.value = false;
  }
});

const { initiateSocialLogin } = useInitiateSocialLogin();
const { startIntegrationLogin } = useIntegrationLogin();

const route = useRoute();
const router = useRouter();

// An invite link carries the invited email plus its inviteCode; the email is locked to the
// invited address only when the code still resolves to that same email server-side.
const inviteCode = typeof route.query.inviteCode === 'string' ? route.query.inviteCode : '';
const inviteEmail = typeof route.query.email === 'string' ? route.query.email.trim() : '';
const inviteCheck = inviteCode && inviteEmail ? await useStudentRoleInvite(inviteCode) : null;
const emailLocked = !!inviteCheck?.value?.invite && inviteCheck.value.invite.email.toLowerCase() === inviteEmail.toLowerCase();

const config = useConfigStore();
const countryStates = computed(() =>
  (config.cluster?.country?.states ?? []).map(state => ({ label: state.name, value: state.id })),
);
// A provider's button shows only where the cluster row lists it.
const integrationProviders = computed(() => config.cluster?.integrationsConfig?.loginButtons ?? []);
const authFlowRef = ref<{
  clearOtp: () => void;
  setEmailError: (message: string) => void;
  setOtpError: (message: string) => void;
  setPasswordError: (message: string) => void;
  setPasswordSetupError: (message: string) => void;
  setMfaChallengeError: (message: string) => void;
  setMfaEnrolError: (message: string) => void;
} | null>(null);

onMounted(() => {
  if (route.query.error === 'social_auth_failed') {
    globalError.value = t('general.auth.socialLoginFailed');
    void router.replace({ path: route.path, query: {} });
  }

  if (route.query.error === 'access_denied') {
    globalError.value = t('general.auth.accessDenied');
    void router.replace({ path: route.path, query: {} });
  }

  if (route.query.error === 'classroom_not_verified') {
    globalError.value = t('general.errors.personalClassroomNotVerified');
    void router.replace({ path: route.path, query: {} });
  }

  if (route.query.sessionExpired) {
    const { show } = useToast();
    show({ variant: 'warning', message: t('general.errors.sessionExpired') });
    void router.replace({ path: route.path, query: {} });
  }
});

const handleSocialLogin = async (provider: SocialLoginProvider | IntegrationLoginProvider) => {
  globalError.value = null;
  loading.value = true;

  // An integration login has its own pipeline; it leaves this page either for the provider or
  // for the failure page, so loading stays on.
  if (isIntegrationLoginProvider(provider)) {
    await startIntegrationLogin(provider);
    return;
  }

  const initiated = await initiateSocialLogin(provider, parseEntryTrackingFromQuery(route.query));

  // On success loading stays true: the browser is hard-navigating to the OAuth provider.
  if (!initiated) {
    globalError.value = t('general.auth.socialLoginFailed');
    loading.value = false;
  }
};

const handleEmailSubmit = async (email: string) => {
  globalError.value = null;
  const { error } = await submitEmail(email);
  if (error) {
    const message = getFirstErrorMessageFromCombinedErrors(t, error);
    authFlowRef.value?.setEmailError(message);
  }
};

const handleOtpSubmit = async (code: string) => {
  globalError.value = null;
  const { flow, error } = await submitOtp(code);

  if (error === 'access-denied') {
    globalError.value = t('general.auth.accessDenied');
    return;
  }

  if (error) {
    const message = typeof error === 'string' ? t('general.unexpectedError') : getFirstErrorMessageFromCombinedErrors(t, error);
    authFlowRef.value?.clearOtp();
    authFlowRef.value?.setOtpError(message);
    return;
  }

  if (flow === LoginFlowType.InvalidCode) {
    authFlowRef.value?.clearOtp();
    authFlowRef.value?.setOtpError(t('general.auth.invalidOtpCode'));
  }
};

const handleOtpResend = async (onComplete: () => void) => {
  globalError.value = null;
  const { error } = await resendLoginCode();

  if (error) {
    authFlowRef.value?.setOtpError(getFirstErrorMessageFromCombinedErrors(t, error));
    return;
  }

  onComplete();
};

const handleMethodChooseOtp = async () => {
  globalError.value = null;
  const { error } = await chooseOtpMethod();
  if (error) {
    globalError.value = getFirstErrorMessageFromCombinedErrors(t, error);
  }
};

const handleMethodChoosePassword = () => {
  globalError.value = null;
  choosePasswordMethod();
};

const handlePasswordSubmit = async (password: string) => {
  globalError.value = null;
  const { flow, error } = await submitPassword(password);

  if (error === 'access-denied') {
    globalError.value = t('general.auth.accessDenied');
    return;
  }

  if (error) {
    const message = typeof error === 'string' ? t('general.unexpectedError') : getFirstErrorMessageFromCombinedErrors(t, error);
    authFlowRef.value?.setPasswordError(message);
    return;
  }

  if (flow === LoginFlowType.InvalidCredentials) {
    authFlowRef.value?.setPasswordError(t('kui.auth.invalidCredentials'));
  }
};

const handleForgotPassword = async () => {
  globalError.value = null;
  const result = await forgotPassword();

  if (result.error === 'access-denied') {
    globalError.value = t('general.auth.accessDenied');
    return;
  }

  if (result.error) {
    const message = typeof result.error === 'string' ? t('general.unexpectedError') : getFirstErrorMessageFromCombinedErrors(t, result.error);
    authFlowRef.value?.setPasswordError(message);
  }
};

const handleAccountChosen = async (result: SignInContinuation) => {
  globalError.value = null;
  const { error } = await continueAfterAccountChoice(result);

  if (error === 'access-denied') {
    globalError.value = t('general.auth.accessDenied');
  }
};

const handleConsentSubmit = async () => {
  globalError.value = null;
  const { error } = await submitConsent();

  if (error) {
    globalError.value = getFirstErrorMessageFromCombinedErrors(t, error);
  }
};

const handlePasswordSetupSubmit = async (password: string) => {
  globalError.value = null;
  const { error } = await submitPasswordSetup(password);

  if (error) {
    const message = typeof error === 'string' ? error : getFirstErrorMessageFromCombinedErrors(t, error);
    authFlowRef.value?.setPasswordSetupError(message);
  }
};

const handleMfaChallengeSubmit = async (submit: () => Promise<{ error: string | null }>) => {
  globalError.value = null;
  const { error } = await submit();

  if (error === 'access-denied') {
    globalError.value = t('general.auth.accessDenied');
    return;
  }

  if (error) {
    authFlowRef.value?.setMfaChallengeError(error);
  }
};

const handleMfaEnrolSubmit = async (code: string) => {
  globalError.value = null;
  const { error } = await mfaEnrolSubmitCode(code);

  if (error) {
    authFlowRef.value?.setMfaEnrolError(error);
  }
};

const handleMfaRecoveryCodesSaved = async () => {
  globalError.value = null;
  const { error } = await mfaRecoveryCodesSaved();

  if (error === 'access-denied') {
    globalError.value = t('general.auth.accessDenied');
  }
};

const handleDetailsSubmit = async (stateId: string, birthDate: Date) => {
  globalError.value = null;
  const { error } = await submitDetails(stateId, birthDate);

  if (error) {
    globalError.value = getFirstErrorMessageFromCombinedErrors(t, error);
  }
};
</script>

<template>
  <KUIUnifiedAuthFlow
    ref="authFlowRef"
    data-testid="auth-form"
    :current-step="currentStep"
    :auth-state="authState"
    :is-loading="isRedirecting"
    :is-submitting="loading"
    :signup-ready="roleContextReady"
    :global-error="globalError"
    :direction="direction"
    :country-states="countryStates"
    :integration-providers="integrationProviders"
    :initial-otp-code="prefilledOtpCode"
    :initial-email="emailLocked ? inviteEmail : authState.email"
    :email-locked="emailLocked"
    :terms-path="door.termsHref"
    :password-forgot-action="config.isSchoolsCluster ? PasswordForgotAction.Reset : PasswordForgotAction.Code"
    :help-text="door.helpText"
    :disclaimer="disclaimer"
    :show-social="door.showSocial"
    :step-images="door.stepImages"
    :layout="door.layout"
    :register-disabled-help-text="door.registerDisabledHelpText"
    :password-setup-description="door.passwordSetupDescription"
    :mfa-expired="mfaExpired"
    :mfa-signed-in="mfaSignedIn"
    :mfa-enrolment="mfaEnrolment"
    :mfa-load-failed="mfaLoadFailed"
    :mfa-recovery-codes="mfaRecoveryCodes"
    @email-submit="handleEmailSubmit"
    @social-login="handleSocialLogin"
    @method-choose-otp="handleMethodChooseOtp"
    @method-choose-password="handleMethodChoosePassword"
    @method-back="reset"
    @password-submit="handlePasswordSubmit"
    @password-forgot="handleForgotPassword"
    @password-back="reset"
    @otp-submit="handleOtpSubmit"
    @otp-resend="handleOtpResend"
    @otp-back="reset"
    @consent-submit="handleConsentSubmit"
    @name-submit="submitName"
    @details-submit="handleDetailsSubmit"
    @details-back="goBack"
    @password-setup-submit="handlePasswordSetupSubmit"
    @password-setup-skip="skipPasswordSetup"
    @welcome-continue="finishWelcome"
    @mfa-submit-code="(code) => handleMfaChallengeSubmit(() => mfaSubmitCode(code))"
    @mfa-submit-recovery-code="(code) => handleMfaChallengeSubmit(() => mfaSubmitRecoveryCode(code))"
    @mfa-start-again="reset({ keepPrefilledEmail: false })"
    @mfa-sign-out="mfaSignOut"
    @mfa-enrol-retry="beginMfaEnrolment"
    @mfa-enrol-submit-code="handleMfaEnrolSubmit"
    @mfa-recovery-codes-saved="handleMfaRecoveryCodesSaved"
    @register-disabled-back="reset({ keepPrefilledEmail: false })"
  >
    <template #choose-account>
      <CoreAuthLoginChooseAccount
        v-model:loading="loading"
        :role-type="props.roleType"
        :selected-entity="selectedEntity"
        @chosen="handleAccountChosen"
        @restart="restartSignIn"
      />
    </template>
    <template #school>
      <UnifiedAuthStepSchool :signed-up="signedUp" @done="submitSchool" />
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </KUIUnifiedAuthFlow>
</template>
